import { Injectable, NgZone } from '@angular/core';
import { generateUUID } from '@app/origami-api/utils/common.utils';

@Injectable()
export class InstancesManagerService {
  private readonly HEARTBEAT_INTERVAL = 60 * 1000;
  private readonly registryKey = 'InstancesRegistry';
  private readonly storagePrefix = 'ti-';

  private instanceId: string;
  private heartbeatIntervalId;

  constructor(private zone: NgZone) {
    // nothing here for now
    // probably we need to sync the registry at the startup to delete orphaned records if any
  }

  public start() {
    this.syncRegistry();

    this.heartbeat();
    this.zone.runOutsideAngular(() => {
      this.heartbeatIntervalId = setInterval(this.heartbeat.bind(this), this.HEARTBEAT_INTERVAL);
    });
  }

  private stop() {
    clearInterval(this.heartbeatIntervalId);
  }

  private registerInstance() {
    if (!this.instanceId) {
      this.instanceId = generateUUID();
    }

    let instances = this.getStorageItem<string[]>(this.registryKey) || [];

    if (instances.includes(this.instanceId)) {
      //console.log('GUID COLLISION', this.instanceId);
    } else {
      instances.push(this.instanceId);
    }

    this.setStorageItem(this.registryKey, instances);
  }

  public unregisterInstance(id?) {
    id = id || this.instanceId;

    if (!id) {
      //console.log('NO GUID');
      return;
    }

    if (id === this.instanceId) {
      this.stop();
    }

    let instances = this.getStorageItem<string[]>(this.registryKey) || [];

    if (!instances.includes(id)) {
      //console.log('NO GUID IN REGISTRY', id);
    } else {
      //instances.remove(id);
      instances.splice(instances.indexOf(id), 1);
    }

    this.setStorageItem(this.registryKey, instances);

    this.setStorageItem(this.getKey(id));
  }

  public noMoreInstances() {
    this.setStorageItem('DEBUG', this.getStorageItem<string[]>(this.registryKey));
    return (this.getStorageItem<string[]>(this.registryKey) || []).length < 1;
  }

  private heartbeat() {
    if (!this.instanceId) {
      this.registerInstance();
    }

    this.setStorageItem(this.getKey(this.instanceId), {
      lastHeartbeat: +new Date(),
    });

    this.syncInstances();
  }

  private getKey(id) {
    return `${this.storagePrefix}${this.registryKey}-${id}`;
  }

  // one way sync
  // this is a lightweight logic so we can do that every HEARTBEAT_INTERVAL
  private syncInstances() {
    let instancesToKill = [];

    (this.getStorageItem<string[]>(this.registryKey) || []).forEach((guid) => {
      let instanceData = this.getStorageItem<any>(this.getKey(guid));
      if (
        instanceData &&
        instanceData.lastHeartbeat &&
        instanceData.lastHeartbeat >= +new Date() - this.HEARTBEAT_INTERVAL * 2
      ) {
        // keeping this instance
      } else {
        instancesToKill.push(guid);
      }
    });

    instancesToKill.forEach(this.unregisterInstance.bind(this));
  }

  // one-way sync
  // this logic is quite heavy, so let's do it on the app start, one time
  private syncRegistry() {
    if (!window.localStorage) {
      return;
    }

    let prefix = this.storagePrefix + this.registryKey + '-';

    let existingGuids = [];
    let key;

    for (let i = 0; i < localStorage.length; i++) {
      key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        existingGuids.push(key.substr(prefix.length));
      }
    }

    let registry = this.getStorageItem<string[]>(this.registryKey) || [];

    existingGuids.forEach((guid) => {
      if (!registry.includes(guid)) {
        this.setStorageItem(this.getKey(guid));
      }
    });
  }

  private getStorageItem<T>(key: string): T {
    if (!key.startsWith(this.storagePrefix)) {
      key = this.storagePrefix + key;
    }

    const item = localStorage.getItem(key);
    if (!item) {
      return null;
    }

    let result: T = null;

    try {
      result = JSON.parse(item);
    } catch (e) {
      //
    }

    return result;
  }

  private setStorageItem(key: string, value?: any) {
    if (!key.startsWith(this.storagePrefix)) {
      key = this.storagePrefix + key;
    }

    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }
}
